import parse from "html-react-parser";
import { lazy } from "react";
import { Link } from "react-router-dom";
import { DEFAULT_FILTER_VALUE } from "./const";

export const isBookingOpen = (config) => {
  const now = Date.now();
  return now > Date.parse(config.bookingOpenFrom) && now < Date.parse(config.bookingOpenUntil);
};

export const applyFilters = (parks, filters) => {
  return parks.filter((park) => {
    return Object.entries(filters).every(([key, value]) => {
      return value === DEFAULT_FILTER_VALUE || park[key]?.trim() === value?.trim();
    });
  });
};

export function parkNameWithoutOperator(parkName, operator) {
  return parkName?.replace(operator, "").trim() || parkName?.trim();
}

export const transformHtmlWithLinks = (htmlString) => {
  const transform = (node) => {
    if (node.type === "tag" && node.name === "a") {
      const url = node.attribs.href;
      const isExternal = url?.startsWith("http") || url?.startsWith("//");

      if (isExternal) {
        return (
          <a href={url} target="_blank" rel="noopener noreferrer">
            {node.children[0]?.data}
          </a>
        );
      } else if (url.startsWith("mailto:")) {
        // Mailto links
        return <a href={url}>{node.children[0]?.data}</a>;
      }

      return <Link to={url}>{node.children[0]?.data}</Link>;
    }
  };

  return parse(htmlString, { replace: transform });
};

export const isDataPopulated = (data) => {
  if (!data) return false; // null or undefined
  const obj = Array.isArray(data) ? { ...data } : data;
  return Object.keys(obj).length > 0;
};

export const getUniqueEntriesOf = (data, attribute) => [...new Set(data.map((park) => park[attribute]))];

export const toggleStateWithGA = (stateSetter, GAFunction) => {
  stateSetter((prev) => {
    const newState = !prev;
    GAFunction(newState);
    return newState;
  });
};

export const kebabToTitle = (string) =>
  string
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

export const formatAddressComponent = (component) =>
  component
    ?.split(",")
    .map((part) => part.trim())
    .join(", ") || "";

export const isExternalLink = (url) => {
  try {
    const parsed = new URL(url);
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
};

// Abstract the logic for lazy export default components
export const lazyNamed = (importFn, exportName) =>
  lazy(() =>
    importFn().then((module) => ({
      default: module[exportName],
    })),
  );

export const capitalize = (string) => string.charAt(0).toUpperCase() + string.slice(1);

/**
 * Returns the next index in a circular array
 * @param {number} index - current index
 * @param {number} arrayLength - length of the array
 * @param {'next' | 'prev'} direction - direction to move
 * @returns {number} - next index, wrapped around
 */
export const getCircularIndex = (index, arrayLength, direction) => {
  if (arrayLength === 0) return -1;

  if (direction === "next") {
    return (index + 1) % arrayLength;
  } else if (direction === "prev") {
    return (index - 1 + arrayLength) % arrayLength;
  } else {
    throw new Error("Direction must be 'next' or 'prev'");
  }
};

export const pluralize = (timeUnit, timeLeft) => {
  return timeLeft === 1 ? timeUnit : `${timeUnit}s`;
};
